import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { IQuoteListState, IBaseSearchFormSchema } from '~/purchase/types';
import { baseSearchFormSchema } from '~/purchase/schemas';
import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import {
  DEFAULT_VEHICLE_TYPE,
  PurchaseSearchHelpers,
  QuoteHelpers,
  SearchHistoryHelpers,
} from '~/purchase/helpers';
import { useNavigate, useSearchParams } from 'react-router';
import { serialize, useOnUpdate, useStateMerger } from '@wyz/utils';
import { useManageQuotes, useManageSearchHistory } from '~/purchase/api';
import { IUserCustomer } from '~/common/types';
import { notify } from '@wyz/ui';
import { useI18n } from '~/common/context';

const DEFAULT_PRODUCT_LIST_STATE: IQuoteListState = {
  searchQuery: undefined,
  filters: {
    segments: [],
    brands: [],
    seasons: [],
    vehicleTypes: [],
    homologations: [],
    availability: [],
    inStock: false,
    proximityStock: false,
    specificities: [],
    j0: false,
    extraBonus: false,
    destocking: [],
  },
  sort: PurchaseSearchHelpers.DEFAULT_SEARCH_SORT,
};

type Args = {
  customer: IUserCustomer;
  defaultValues?: Partial<IBaseSearchFormSchema>;
  express?: boolean;
};
export const useQuoteSearchForm = ({
  customer,
  defaultValues,
  express = false,
}: Args) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const manageSearchHistory = useManageSearchHistory({
    create: { enabled: true },
  });
  const manageQuotes = useManageQuotes({
    createExpress: { enabled: true },
  });

  const [listState, setter] = useStateMerger<IQuoteListState>(
    DEFAULT_PRODUCT_LIST_STATE,
  );
  const methods = useForm<IBaseSearchFormSchema>({
    resolver: zodResolver(baseSearchFormSchema),
    defaultValues: QuoteHelpers.getQuoteSearchDefaultValues({
      seasons: customer.settingTemplate?.generalSetting?.defaultSeasons ?? [], // should be before qs defaults since it can be override by qs
      ...PurchaseSearchHelpers.parseSearchFromQs(searchParams),
      ...defaultValues,
      vehicleTypeGroup:
        customer.settingTemplate?.vehicleTypeGroups.find(
          (group) => group.name === DEFAULT_VEHICLE_TYPE,
        )?.name || customer.settingTemplate?.vehicleTypeGroups[0]?.name,
    }),
  });
  const watchedVehicleTypeGroup = useWatch({
    control: methods.control,
    name: 'vehicleTypeGroup',
  });

  const vehicleTypes = customer.settingTemplate?.vehicleTypeGroups;
  const relatedTypes = useMemo(
    () =>
      watchedVehicleTypeGroup && vehicleTypes
        ? vehicleTypes.find((item) => item.name === watchedVehicleTypeGroup)
            ?.vehicleTypeIds
        : [],
    [vehicleTypes, watchedVehicleTypeGroup],
  );

  const handleOnSubmitSearch = useCallback(
    (query: Partial<IQuoteListState>) => {
      if (express) {
        if (query.searchQuery) {
          manageQuotes.createExpress.mutate(
            { customerId: customer.id, searchQuery: query.searchQuery },
            {
              onSuccess: ({ data }) => {
                notify.success(t('app.common.messages.success'), {
                  id: 'toast-success-express-quote-create',
                });
                void navigate(
                  `/quotes/history?${serialize({ searchQuery: data.reference })}`,
                );
              },
            },
          );
        }
      } else {
        if (query.searchQuery) {
          manageSearchHistory.create.mutate(
            SearchHistoryHelpers.buildQuotePayload(query.searchQuery),
          );
        }
        setter(
          query.searchQuery
            ? { filters: DEFAULT_PRODUCT_LIST_STATE.filters, ...query }
            : query,
        );
        void navigate({
          pathname: '/quotes/search/result',
          search: serialize(
            PurchaseSearchHelpers.serializeSearchToQs(query.searchQuery),
          ),
        });
      }
    },
    [
      manageSearchHistory.create,
      manageQuotes.createExpress,
      customer.id,
      navigate,
      t,
      setter,
      express,
    ],
  );

  const setValue = useEffectEvent(methods.setValue);
  const resetForm = useEffectEvent(() => {
    methods.reset({
      ...QuoteHelpers.getQuoteSearchDefaultValues(),
      vehicleTypeGroup: methods.getValues('vehicleTypeGroup'),
      seasons: customer.settingTemplate?.generalSetting?.defaultSeasons ?? [],
    });
  });

  useOnUpdate(() => {
    if (customer.id) {
      setter(DEFAULT_PRODUCT_LIST_STATE);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      resetForm();
    }
  }, [customer.id, setter]);

  useEffect(() => {
    if (watchedVehicleTypeGroup && relatedTypes) {
      setValue('vehicleTypes', relatedTypes);
    }
  }, [watchedVehicleTypeGroup, relatedTypes]);

  return {
    vehicleTypes: relatedTypes ?? [],
    vehicleTypesGroups: customer.settingTemplate?.vehicleTypeGroups ?? [],
    methods,
    listState,
    setter,
    onSubmit: handleOnSubmitSearch,
    createExpress: manageQuotes.createExpress,
  };
};
